import React, { useEffect } from "react";
import { Form, Input, Spin } from "antd";
import { CheckboxOptionType } from "antd/es/checkbox";
import { lowerCase } from "lodash";
import { DropdownInput } from "components";
import { EComModal } from "components/EComModal";
import { AiccLessonEntryPayload } from "types/model/aiccLearningAssets";
import { toLessonEntryPayload } from "./toLessonEntryPayload";

interface Props {
  visible: boolean;
  title: string;
  textLabel: string;
  levelOptions: CheckboxOptionType[];
  getLessonOptions: (level?: number) => CheckboxOptionType[];
  hasTransliteration: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (payload: AiccLessonEntryPayload) => void;
}

function LessonEntryModal({
  visible,
  title,
  textLabel,
  levelOptions,
  getLessonOptions,
  hasTransliteration,
  isSaving,
  onCancel,
  onSubmit,
}: Props) {
  const [form] = Form.useForm<AiccLessonEntryPayload>();

  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [form, visible]);

  const handleOk = () => form.validateFields().then((values) => onSubmit(toLessonEntryPayload(values)));

  const handleValuesChange = (changed: Partial<AiccLessonEntryPayload>) => {
    if (changed.level !== undefined) {
      form.setFieldsValue({ lesson: undefined });
    }
  };

  return (
    <EComModal visible={visible} title={title} okText="Save" onOk={handleOk} onCancel={onCancel} isSaving={isSaving}>
      <Spin spinning={isSaving} size="large">
        <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
          <Form.Item label="Level" name="level" rules={[{ required: true, message: "Please select a level." }]}>
            <DropdownInput options={levelOptions} placeholder="Level" mode="single" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.level !== next.level}>
            {({ getFieldValue }) => (
              <Form.Item label="Lesson" name="lesson" rules={[{ required: true, message: "Please select a lesson." }]}>
                <DropdownInput options={getLessonOptions(getFieldValue("level"))} placeholder="Lesson" mode="single" />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item
            label={textLabel}
            name="text"
            rules={[{ required: true, whitespace: true, message: `Please enter a ${lowerCase(textLabel)}.` }]}
          >
            <Input placeholder={textLabel} />
          </Form.Item>
          <Form.Item
            label="Translation"
            name="translation"
            rules={[{ required: true, whitespace: true, message: "Please enter a translation." }]}
          >
            <Input placeholder="Translation" />
          </Form.Item>
          {hasTransliteration && (
            <Form.Item label="Transliteration" name="transliteration">
              <Input placeholder="Transliteration (optional)" />
            </Form.Item>
          )}
        </Form>
      </Spin>
    </EComModal>
  );
}

export default LessonEntryModal;
